import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

const FAQ_ITEMS = [
  {
    question: "What is an IVA (Individual Voluntary Arrangement)?",
    answer: "An IVA is a legally binding agreement between you and your creditors to pay back your debts over a fixed period, typically 5 years. Once the IVA is complete, any remaining unsecured debt included in the arrangement is written off. IVAs are a government-backed debt solution regulated by the Insolvency Service."
  },
  {
    question: "How much debt can be written off with an IVA?",
    answer: "The amount written off varies depending on your individual circumstances, but typically between 50-85% of your unsecured debt could be written off. The exact amount depends on factors like your income, essential living costs, and total debt amount."
  },
  {
    question: "Who qualifies for an IVA?",
    answer: "To qualify for an IVA, you generally need: at least £5,000 in unsecured debt, owe money to 2 or more creditors, be able to make regular monthly payments, and be a UK resident. Each case is assessed individually to determine the best solution for your circumstances."
  },
  {
    question: "Will an IVA affect my credit score?",
    answer: "Yes, an IVA will be recorded on your credit file for 6 years from the start date. During this time, it may be more difficult to obtain credit. However, many people find that managing their debt through an IVA helps them rebuild their finances and eventually improve their credit score."
  },
  {
    question: "What debts can be included in an IVA?",
    answer: "Most unsecured debts can be included in an IVA, such as: credit cards, personal loans, overdrafts, store cards, catalogue debts, and some HMRC debts. Secured debts like mortgages, student loans, and child maintenance cannot be included."
  },
  {
    question: "How long does the IVA process take?",
    answer: "Once you've completed your free assessment and decided to proceed, the IVA proposal is typically prepared and sent to creditors within 2-4 weeks. Creditors then have 14 days to vote on the proposal. If approved, your IVA will officially begin and you'll start making your agreed monthly payments."
  },
  {
    question: "Will my employer or landlord find out about my IVA?",
    answer: "IVAs are recorded on the Individual Insolvency Register, which is a public record. However, employers don't routinely check this register. Your landlord won't be automatically notified unless you have rent arrears included in the IVA. We can discuss any specific concerns during your free assessment."
  },
  {
    question: "What happens if I can't make my IVA payments?",
    answer: "If your circumstances change, it's important to contact your Insolvency Practitioner immediately. Options may include: a payment break, reduced payments, or in some cases, the IVA terms may need to be modified. We work with you to find solutions that keep your IVA on track."
  }
];

const FAQSection = () => {
  return (
    <section className="pt-20 pb-12 md:pt-24 md:pb-16 bg-[#F8FAFC]" data-testid="faq-section">
      <div className="container-main">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-[#059669] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <HelpCircle className="w-4 h-4" />
            Common Questions
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Get answers to the most common questions about IVAs and debt management
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-4">
            {FAQ_ITEMS.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-xl border border-slate-200 px-6 overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-[#0F172A] hover:text-[#10B981] py-5 [&[data-state=open]]:text-[#10B981]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* CTA Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="bg-white rounded-2xl p-8 max-w-2xl mx-auto border border-slate-200">
            <h3 className="text-xl font-bold text-[#0F172A] mb-2">
              Still have questions?
            </h3>
            <p className="text-slate-600 mb-4">
              Our friendly team is here to help. Get your free, no-obligation assessment today.
            </p>
            <p className="text-sm text-slate-500">
              Free advice line: <span className="font-semibold text-[#0F172A]">hello@solvedebts.co.uk</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
